import type { BlameHunk, Remote } from 'codesearch-backend'
import { createSignal, Show } from 'solid-js'
import { useCommit } from '../store/view.ts'
import { copy } from '../utils/clipboard.ts'
import { commitUrl } from '../utils/forge.ts'
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover.tsx'

function stamp(seconds: number): string {
  return new Date(seconds * 1000).toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/*
  code search's blame gutter opens a "Commit Details" card: the hash as a link, who
  and when, the full message in a scrollable box, and an action to keep digging.
  the message is fetched only when the card opens - blame already ships one line of
  summary per hunk and a full message per hunk would be most of the payload
*/
export function CommitPopover(props: {
  repo: string
  hunk: BlameHunk
  ordinal: number
  remote: Remote | null | undefined
  /** re-blame the file as it was before this commit */
  onBlameBefore: (sha: string) => void
}) {
  const [open, setOpen] = createSignal(false)
  const commit = useCommit(() => props.repo, () => (open() ? props.hunk.commit : undefined))
  const url = () => commitUrl(props.remote, props.hunk.commit)

  return (
    <Popover open={open()} onOpenChange={setOpen}>
      <PopoverTrigger
        class="blame-rev w-9 shrink-0 cursor-pointer text-right tabular-nums hover:underline"
        title={`${props.hunk.short} ${props.hunk.summary}`}
      >
        {`#${props.ordinal}`}
      </PopoverTrigger>

      <PopoverContent class="w-120 p-0">
        <div class="border-b border-border px-3 py-2 text-sm font-medium">Commit Details</div>

        <div class="px-3 py-2 text-xs">
          <div class="flex flex-wrap items-center gap-1">
            <Show
              when={url()}
              fallback={<span class="font-medium font-mono">{`Commit ${props.hunk.short}`}</span>}
            >
              {href => (
                <a href={href()} target="_blank" rel="noreferrer" class="blame-rev font-medium font-mono hover:underline">
                  {`Commit ${props.hunk.short}`}
                </a>
              )}
            </Show>
            <span class="text-muted-foreground">
              {' : '}
              {props.hunk.email || props.hunk.author}
              {' @ '}
              {stamp(props.hunk.time)}
            </span>
          </div>

          <div class="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-mono">
            <Show when={commit.data} fallback={<span class="text-muted-foreground">loading...</span>}>
              {message => message().message.trimEnd()}
            </Show>
            <Show when={commit.isError}>
              <span class="text-destructive">could not load commit</span>
            </Show>
          </div>
        </div>

        <div class="flex items-center gap-3 border-t border-border px-3 py-1.5 text-xs">
          <button
            type="button"
            class="cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={() => {
              setOpen(false)
              props.onBlameBefore(props.hunk.commit)
            }}
          >
            View blame prior to this change
          </button>
          <div class="flex-1" />
          <button
            type="button"
            class="cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={() => void copy(props.hunk.commit, 'commit copied')}
          >
            Copy hash
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
